import { ChevronRight, Clock, Gift } from "lucide-react";

import { Button } from "@krea/shared/design-system/Button";
import { cn } from "@krea/shared/utils";

import type { IOfferAttractivenessTrialState } from "./trial";
import { formatDaysLeft, formatTrialEndDate } from "./trial";

interface OfferAttractivenessTrialStripProps {
  trial: IOfferAttractivenessTrialState;
  onKeepClick: () => void;
}

/**
 * Trial banner that bleeds to the edges of the surrounding DetailsCard, which pads
 * `p-4 xl:p-6` — hence the matching negative margins at both breakpoints.
 *
 * Two treatments: a calm purple strip for most of the trial, and an orange one once
 * `trial.isUrgent` flips (5 days left, counting today). The urgent version promotes the
 * countdown into the copy and the CTA to a primary button.
 */
const OfferAttractivenessTrialStrip = ({
  trial,
  onKeepClick,
}: OfferAttractivenessTrialStripProps) => {
  const daysText = formatDaysLeft(trial.daysRemaining);
  const endText = trial.endsOn ? formatTrialEndDate(trial.endsOn) : null;

  // The icon sits OUTSIDE the wrapping group, so `items-center` centres it against the
  // strip's full height and the copy and CTA share one left edge — a wrapped CTA lines up
  // with the text rather than the icon.
  //
  // The negative margins cancel the DetailsCard's `p-4 xl:p-6` so the strip is flush with
  // the card edge at BOTH breakpoints — dropping `xl:-mt-6` leaves an 8px gap above it.
  // The explicit width is needed because a <button> does not stretch like a block element.
  const layout =
    "-mx-4 -mt-4 mb-5 flex w-[calc(100%+2rem)] items-center gap-2.5 rounded-t-xl px-4 py-2 text-left xl:-mx-6 xl:-mt-6 xl:w-[calc(100%+3rem)] xl:px-6";

  // `flex-wrap` here, not on the strip: the CTA drops below the copy, both flush to this
  // column's left edge, while the icon stays put and centred.
  const column =
    "flex min-w-0 flex-1 flex-wrap items-center justify-between gap-x-2.5 gap-y-2";

  if (trial.isUrgent) {
    return (
      <div className={cn(layout, "bg-orange/15")}>
        <Clock className="size-4 shrink-0 text-orange" strokeWidth={2.5} />
        <span className={column}>
          <span className="min-w-0 text-sm font-semibold text-black">
            {endText
              ? `Free trial ends ${endText} - ${daysText}`
              : `Free trial - ${daysText}`}
          </span>
          <Button
            size="sm"
            className="shrink-0 whitespace-nowrap"
            onClick={onKeepClick}
          >
            Keep the feature
          </Button>
        </span>
      </div>
    );
  }

  // The calm strip has no separate CTA — the whole bar is the control, so it is a single
  // <button> rather than a row containing one. One tab stop, one hover target.
  return (
    <button
      type="button"
      onClick={onKeepClick}
      className={cn(
        layout,
        // Tailwind v4 resets <button> to cursor:default; the design-system Button opts
        // back in explicitly, and this raw one has to as well.
        "cursor-pointer bg-primary-150 hover:bg-primary-100",
      )}
    >
      <Gift className="size-4 shrink-0 text-primary" />
      <span className={column}>
        <span className="min-w-0 text-sm font-semibold text-primary">
          {endText ? `Free trial until ${endText}` : "Free trial"}
        </span>
        <span className="flex shrink-0 items-center gap-1 whitespace-nowrap text-sm font-semibold text-primary">
          {daysText}
          <ChevronRight className="size-3.5" />
        </span>
      </span>
    </button>
  );
};

export { OfferAttractivenessTrialStrip };
